// 唯一读取 NEXT_PUBLIC_BACKEND_URL 的地方——其它代码一律从这里 import BACKEND_ORIGIN，
// 不要再各自写 process.env.NEXT_PUBLIC_BACKEND_URL，避免漏加 fallback 导致 URL 里出现字面 "undefined"。
export const BACKEND_ORIGIN = process.env.NEXT_PUBLIC_BACKEND_URL || ""
const BACKEND = `${BACKEND_ORIGIN}/api`

function getToken(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem("token")
}

function authHeaders(): HeadersInit {
  const token = getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// 后端非预期报错（比如反向代理/网关级500、超时）返回的是纯文本，不是
// {"detail": "..."}这种JSON格式——直接res.json()会抛SyntaxError（"Unexpected
// token..."这种看不懂的浏览器原生报错），把真正的错误信息盖住。这个函数
// 兜底：JSON解析失败就退回读纯文本，永远不会因为响应体不是JSON而崩溃
async function safeJson(res: Response): Promise<any> {
  const text = await res.text()
  try {
    return JSON.parse(text)
  } catch {
    return { detail: text ? text.slice(0, 300) : `服务器错误（${res.status}）` }
  }
}

export function saveToken(token: string) {
  localStorage.setItem("token", token)
}

export function clearToken() {
  localStorage.removeItem("token")
}

export async function fetchMe() {
  const res = await fetch(`${BACKEND}/auth/me`, { headers: authHeaders() })
  if (!res.ok) {
    // token 失效（比如数据库重建后旧 token 指向的用户已经不存在）就清掉，
    // 避免无效 token 一直留在 localStorage 里反复拖累后续请求
    clearToken()
    return null
  }
  return res.json()
}

export interface ModelOption {
  model: string
  provider: string
  can_disable_reasoning: boolean
  supports_images: boolean
}

export async function fetchModels(): Promise<ModelOption[]> {
  const res = await fetch(`${BACKEND}/models`, { headers: authHeaders() })
  const data = await res.json()
  return data.models
}

export async function fetchConversations() {
  const res = await fetch(`${BACKEND}/conversations`, { headers: authHeaders() })
  return res.json()
}

export async function fetchConversation(id: string) {
  const res = await fetch(`${BACKEND}/conversations/${id}`, { headers: authHeaders() })
  return res.json()
}

export async function renameConversation(id: string, title: string) {
  const res = await fetch(`${BACKEND}/conversations/${id}`, {
    method: "PATCH",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || "重命名失败")
  }
  return res.json()
}

export async function deleteConversation(id: string) {
  const res = await fetch(`${BACKEND}/conversations/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || "删除失败")
  }
}

export async function fetchRagStatus(pipelineType: "text" | "pixel" = "text"): Promise<{
  available: boolean
  missing_pipeline: boolean
  missing_reranker: boolean
}> {
  const res = await fetch(`${BACKEND}/rag-status?pipeline_type=${pipelineType}`, { headers: authHeaders() })
  return res.json()
}

// 聊天框只需要"来源"这种轻量信息（帮用户验证答案可信度），不包含分数/plan/耗时——
// 完整技术细节只在 RAG 查询日志页面（engineer/admin 专属）里看
export interface Source {
  evidence_id: string
  preview: string
  source?: { title?: string | null; page?: number | null; source_id?: string | null } | null
  is_image?: boolean
}

// pixel证据的页面截图——不能直接用 <img src> 挂后端URL，这个接口要求登录token，
// img标签没法带 Authorization header。这里手动fetch（带上token）再转成一个临时
// 本地地址给<img>用。用完之后要调 URL.revokeObjectURL 释放，调用方负责。
// (doc_id, page) 是稳定的（同一页文档的图内容不会变），浏览器对fetch的HTTP缓存
// 本来就会生效，不需要在这里另外手写一层缓存
export async function fetchPixelImageBlobUrl(docId: string, page: number): Promise<string> {
  const res = await fetch(`${BACKEND}/pixel-images/${docId}/${page}`, { headers: authHeaders() })
  if (!res.ok) throw new Error(`fetch pixel image failed: ${res.status}`)
  const blob = await res.blob()
  return URL.createObjectURL(blob)
}

export async function streamChat(
  message: string,
  model: string,
  conversationId: string | null,
  onChunk: (chunk: string, convId: string, sources?: Source[]) => void,
  useRag: boolean = false,
  pipelineId: string | null = null,
  rerankerId: string | null = null,
  disableReasoning: boolean = false,
  pipelineType: "text" | "pixel" = "text",
  signal?: AbortSignal
) {
  const res = await fetch(`${BACKEND}/chat`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      message,
      model,
      conversation_id: conversationId,
      use_rag: useRag,
      pipeline_id: pipelineId,
      reranker_id: rerankerId,
      disable_reasoning: disableReasoning,
      pipeline_type: pipelineType,
    }),
    signal,
  })

  const reader = res.body!.getReader()
  const decoder = new TextDecoder()

  while (true) {
    // signal触发abort时，reader.read()会抛AbortError，直接往外抛给调用方——
    // 调用方（ChatWindow）负责判断是不是自己主动abort的，不是当成真正的错误处理
    const { done, value } = await reader.read()
    if (done) break
    const text = decoder.decode(value)
    for (const line of text.split("\n")) {
      if (line.startsWith("data: ")) {
        try {
          const data = JSON.parse(line.slice(6))
          onChunk(data.content, data.conversation_id, data.sources)
        } catch {
          // ignore
        }
      }
    }
  }
}

export async function fetchAdminUsers() {
  const res = await fetch(`${BACKEND}/admin/users`, { headers: authHeaders() })
  return res.json()
}

export async function updateAdminUser(
  userId: string,
  body: { is_active?: boolean; is_admin?: boolean; role?: string; tenant_id?: string; group_ids?: string[] }
) {
  const res = await fetch(`${BACKEND}/admin/users/${userId}`, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || "操作失败")
  }
}

// ── Pipeline ──────────────────────────────────────────────────────────────

export async function fetchPipelineOptions() {
  const res = await fetch(`${BACKEND}/pipelines/options`, { headers: authHeaders() })
  return res.json()
}

export async function fetchPipelines() {
  const res = await fetch(`${BACKEND}/pipelines`, { headers: authHeaders() })
  return res.json()
}

export async function fetchRerankers() {
  const res = await fetch(`${BACKEND}/reranker-configs`, { headers: authHeaders() })
  return res.json()
}

export async function createPipeline(body: object) {
  const res = await fetch(`${BACKEND}/pipelines`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "保存失败")
  return data
}

export async function updatePipeline(id: string, body: object) {
  const res = await fetch(`${BACKEND}/pipelines/${id}`, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "保存失败")
  return data
}

export async function deletePipeline(id: string) {
  const res = await fetch(`${BACKEND}/pipelines/${id}`, { method: "DELETE", headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.detail || "删除失败")
  }
}

export async function promotePipeline(id: string) {
  const res = await fetch(`${BACKEND}/pipelines/${id}/promote`, {
    method: "POST",
    headers: authHeaders(),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "操作失败")
  return data
}

export async function demotePipeline(id: string) {
  const res = await fetch(`${BACKEND}/pipelines/${id}/demote`, {
    method: "POST",
    headers: authHeaders(),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "操作失败")
  return data
}

export async function lockPipeline(id: string) {
  const res = await fetch(`${BACKEND}/pipelines/${id}/lock`, { method: "POST", headers: authHeaders() })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "操作失败")
  return data
}

export async function unlockPipeline(id: string) {
  const res = await fetch(`${BACKEND}/pipelines/${id}/unlock`, { method: "POST", headers: authHeaders() })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "操作失败")
  return data
}

// ── Evaluation ────────────────────────────────────────────────────────────

export async function importEvalSet(body: object) {
  const res = await fetch(`${BACKEND}/evaluation/sets/import`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await safeJson(res)
  if (!res.ok) throw new Error(data.detail || "导入失败")
  return data
}

export async function fetchEvalSets() {
  const res = await fetch(`${BACKEND}/evaluation/sets`, { headers: authHeaders() })
  return res.json()
}

export async function fetchEvalSetDetail(id: string) {
  const res = await fetch(`${BACKEND}/evaluation/sets/${id}`, { headers: authHeaders() })
  return res.json()
}

export async function createEvalRun(body: object) {
  const res = await fetch(`${BACKEND}/evaluation/runs`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await safeJson(res)
  if (!res.ok) throw new Error(data.detail || "启动评测失败")
  return data
}

export async function fetchEvalRuns(evalSetId?: string) {
  const url = evalSetId ? `${BACKEND}/evaluation/runs?eval_set_id=${evalSetId}` : `${BACKEND}/evaluation/runs`
  const res = await fetch(url, { headers: authHeaders() })
  return res.json()
}

export async function fetchEvalRunDetail(id: string) {
  const res = await fetch(`${BACKEND}/evaluation/runs/${id}`, { headers: authHeaders() })
  return res.json()
}

export async function deleteEvalRun(id: string) {
  const res = await fetch(`${BACKEND}/evaluation/runs/${id}`, { method: "DELETE", headers: authHeaders() })
  if (!res.ok) {
    const data = await safeJson(res)
    throw new Error(data.detail || "删除失败")
  }
}

// ── Reranker Config ───────────────────────────────────────────────────────

export async function fetchRerankerOptions() {
  const res = await fetch(`${BACKEND}/reranker-configs/options`, { headers: authHeaders() })
  return res.json()
}

export async function fetchRerankerConfigs() {
  const res = await fetch(`${BACKEND}/reranker-configs`, { headers: authHeaders() })
  return res.json()
}

export async function createRerankerConfig(body: object) {
  const res = await fetch(`${BACKEND}/reranker-configs`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "保存失败")
  return data
}

export async function updateRerankerConfig(id: string, body: object) {
  const res = await fetch(`${BACKEND}/reranker-configs/${id}`, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "保存失败")
  return data
}

export async function deleteRerankerConfig(id: string) {
  const res = await fetch(`${BACKEND}/reranker-configs/${id}`, { method: "DELETE", headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.detail || "删除失败")
  }
}

export async function promoteRerankerConfig(id: string) {
  const res = await fetch(`${BACKEND}/reranker-configs/${id}/promote`, {
    method: "POST",
    headers: authHeaders(),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "操作失败")
  return data
}

export async function demoteRerankerConfig(id: string) {
  const res = await fetch(`${BACKEND}/reranker-configs/${id}/demote`, {
    method: "POST",
    headers: authHeaders(),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "操作失败")
  return data
}

// ── Provider Models（模型/key 管理，替代手动改 .env）────────────────────────────

export async function fetchProviderModelAdapters() {
  const res = await fetch(`${BACKEND}/provider-models/adapters`, { headers: authHeaders() })
  return res.json()
}

// 不是白名单，只是给输入框做自动补全用的——列出之前已经用过的 provider 拼法
export async function fetchKnownProviders() {
  const res = await fetch(`${BACKEND}/provider-models/known-providers`, { headers: authHeaders() })
  const data = await res.json()
  return (data.providers ?? []) as string[]
}

export async function fetchProviderModels() {
  const res = await fetch(`${BACKEND}/provider-models`, { headers: authHeaders() })
  return res.json()
}

export async function createProviderModel(body: object) {
  const res = await fetch(`${BACKEND}/provider-models`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "保存失败")
  return data
}

export async function updateProviderModel(id: string, body: object) {
  const res = await fetch(`${BACKEND}/provider-models/${id}`, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "保存失败")
  return data
}

export async function deleteProviderModel(id: string) {
  const res = await fetch(`${BACKEND}/provider-models/${id}`, { method: "DELETE", headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.detail || "删除失败")
  }
}

// 新增表单里"测试"按钮用——还没存库，直接拿表单当前填的值测
export async function testProviderModelDraft(body: object) {
  const res = await fetch(`${BACKEND}/provider-models/test`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "测试失败")
  return data as { ok: boolean; message: string; detected?: Record<string, unknown> }
}

// 逐个真实验证每个声明支持的维度，不是测一次连接就假设其他维度也对
export async function testProviderModelDimensions(body: object) {
  const res = await fetch(`${BACKEND}/provider-models/test-dimensions`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "测试失败")
  return data as { results: { dim: number; ok: boolean; message: string }[] }
}

// 明文导出，拿到这份数据 = 拿到所有 provider 的真实 key——调用方必须在触发下载前
// 自己做一次二次确认，这个函数本身不做任何拦截
export async function exportProviderModels() {
  const res = await fetch(`${BACKEND}/provider-models/export`, { headers: authHeaders() })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "导出失败")
  return data
}

export async function fetchProviderModelImportTemplate() {
  const res = await fetch(`${BACKEND}/provider-models/import-template`, { headers: authHeaders() })
  return res.json()
}

export async function importProviderModels(file: File) {
  const formData = new FormData()
  formData.append("file", file)
  const res = await fetch(`${BACKEND}/provider-models/import`, {
    method: "POST",
    headers: authHeaders(), // 不手动加 Content-Type——浏览器要自己带 multipart boundary
    body: formData,
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "导入失败")
  return data as { results: { index: number; ok: boolean; message: string }[] }
}

// 已保存记录的"测试"按钮用——不用把明文 key 再传一次前端
export async function testProviderModel(id: string) {
  const res = await fetch(`${BACKEND}/provider-models/${id}/test`, { method: "POST", headers: authHeaders() })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "测试失败")
  return data as { ok: boolean; message: string; detected?: Record<string, unknown> }
}

// ── 推理关闭候选参数 ──────────────────────────────────────────────────────────
// "关闭推理"的候选请求参数写法（DeepSeek/Qwen这些不一样），管理员自己维护，
// 测试连接时系统会挨个试，不是写死在代码里的

export async function fetchReasoningDisableCandidates() {
  const res = await fetch(`${BACKEND}/reasoning-disable-candidates`, { headers: authHeaders() })
  return res.json()
}

export async function createReasoningDisableCandidate(label: string, params: object) {
  const res = await fetch(`${BACKEND}/reasoning-disable-candidates`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ label, params }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "创建失败")
  return data
}

export async function deleteReasoningDisableCandidate(id: string) {
  const res = await fetch(`${BACKEND}/reasoning-disable-candidates/${id}`, { method: "DELETE", headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.detail || "删除失败")
  }
}

// ── Metadata Templates ───────────────────────────────────────────────────────

export async function fetchMetadataTemplates() {
  const res = await fetch(`${BACKEND}/metadata-templates`, { headers: authHeaders() })
  return res.json()
}

export async function createMetadataTemplate(body: object) {
  const res = await fetch(`${BACKEND}/metadata-templates`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "保存失败")
  return data
}

export async function updateMetadataTemplate(id: string, body: object) {
  const res = await fetch(`${BACKEND}/metadata-templates/${id}`, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "保存失败")
  return data
}

export async function deleteMetadataTemplate(id: string) {
  const res = await fetch(`${BACKEND}/metadata-templates/${id}`, { method: "DELETE", headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.detail || "删除失败")
  }
}

// ── Documents ─────────────────────────────────────────────────────────────

export async function browseDocuments(path?: string) {
  const url = path
    ? `${BACKEND}/documents/browse?path=${encodeURIComponent(path)}`
    : `${BACKEND}/documents/browse`
  const res = await fetch(url, { headers: authHeaders() })
  if (!res.ok) {
    // 后端出错（403/404 等）时响应体是 {detail: "..."}，不是 BrowseResult 形状——
    // 之前这里没检查状态码，把错误体直接当成功数据塞进去，导致 browse.items 是 undefined 直接崩页面
    const body = await res.json().catch(() => null)
    console.error("browseDocuments failed:", res.status, body?.detail)
    return null
  }
  return res.json()
}

export async function fetchDocuments(filters?: { filename?: string; pipeline_id?: string; status?: string; visibility?: string; tenant_id?: string }) {
  const params = new URLSearchParams()
  if (filters?.filename) params.set("filename", filters.filename)
  if (filters?.pipeline_id) params.set("pipeline_id", filters.pipeline_id)
  if (filters?.status) params.set("status", filters.status)
  if (filters?.visibility) params.set("visibility", filters.visibility)
  if (filters?.tenant_id) params.set("tenant_id", filters.tenant_id)
  const qs = params.toString()
  const res = await fetch(`${BACKEND}/documents${qs ? `?${qs}` : ""}`, { headers: authHeaders() })
  return res.json()
}

export async function processDocument(body: object) {
  const res = await fetch(`${BACKEND}/documents/process`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "处理失败")
  return data
}

export async function getFolderPermission(path: string) {
  const res = await fetch(`${BACKEND}/documents/folders/permission?path=${encodeURIComponent(path)}`, {
    headers: authHeaders(),
  })
  return res.json()
}

export async function setFolderPermission(body: { path: string; is_public: boolean; group_ids: string[] }) {
  const res = await fetch(`${BACKEND}/documents/folders/permission`, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "保存失败")
  return data
}

// ── Tenants ───────────────────────────────────────────────────────────────

export async function fetchAdminTenants() {
  const res = await fetch(`${BACKEND}/admin/tenants`, { headers: authHeaders() })
  return res.json()
}

export async function createAdminTenant(name: string) {
  const res = await fetch(`${BACKEND}/admin/tenants`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "创建失败")
  return data
}

export async function deleteAdminTenant(id: string) {
  const res = await fetch(`${BACKEND}/admin/tenants/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || "删除失败")
  }
}

export async function fetchAdminGroups() {
  const res = await fetch(`${BACKEND}/admin/groups`, { headers: authHeaders() })
  return res.json()
}

export async function createAdminGroup(name: string, tenant_id: string) {
  const res = await fetch(`${BACKEND}/admin/groups`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ name, tenant_id }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.detail || "创建失败")
  return data
}

export async function deleteAdminGroup(id: string) {
  const res = await fetch(`${BACKEND}/admin/groups/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || "删除失败")
  }
}

// ── RAG 查询日志（engineer/admin 专属，检索质量监控用，跟聊天界面脱钩）──────────

export async function fetchRagLogs(filters?: {
  user_id?: string
  tenant_id?: string
  pipeline_id?: string
  date_from?: string
  date_to?: string
  limit?: number
  offset?: number
}) {
  const params = new URLSearchParams()
  if (filters?.user_id) params.set("user_id", filters.user_id)
  if (filters?.tenant_id) params.set("tenant_id", filters.tenant_id)
  if (filters?.pipeline_id) params.set("pipeline_id", filters.pipeline_id)
  if (filters?.date_from) params.set("date_from", filters.date_from)
  if (filters?.date_to) params.set("date_to", filters.date_to)
  if (filters?.limit != null) params.set("limit", String(filters.limit))
  if (filters?.offset != null) params.set("offset", String(filters.offset))
  const qs = params.toString()
  const res = await fetch(`${BACKEND}/rag-logs${qs ? `?${qs}` : ""}`, { headers: authHeaders() })
  return res.json()
}

export async function fetchRagLogDetail(id: string) {
  const res = await fetch(`${BACKEND}/rag-logs/${id}`, { headers: authHeaders() })
  return res.json()
}
